import { AsyncPipe } from "@angular/common";
import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from "@angular/core";
import {
    BehaviorSubject,
    debounceTime,
    filter,
    map,
    Subscription,
    switchMap,
    withLatestFrom,
} from "rxjs";

import { Coordinates } from "@hermes/api-model-common";
import { LoadGeoLibrariesServiceModule, MapBounds } from "@hermes/map-utils";

import { AbstractMapComponent } from "../../abstract-base-map.component";
import { MarkerData } from "../../map.type";
import {
    extendBounds,
    getBMapPoint,
    getClosestMarker,
} from "../helpers/bmap.helper";
import { MapBaiduMarkerComponent } from "../map-baidu-marker/map-baidu-marker.component";
import { MapBaiduService } from "../services/map-baidu.service";

export const DEFAULT_ZOOM_LEVEL = 11;

@Component({
    selector: "h-map-baidu-display",
    templateUrl: "./map-baidu-display.component.html",
    styleUrls: ["./map-baidu-display.component.scss"],
    imports: [
        AsyncPipe,
        LoadGeoLibrariesServiceModule,
        MapBaiduMarkerComponent,
    ],
})
export class MapBaiduDisplayComponent
    extends AbstractMapComponent
    implements OnInit, OnDestroy, AfterViewInit
{
    @ViewChild("mapinstance", { static: false })
    private mapInstance!: ElementRef;

    public markersInBounds$ = new BehaviorSubject<MarkerData[]>([]);

    public subscription: Subscription = new Subscription();

    public BMAP_ANCHOR_BOTTOM_RIGHT: number = 3;
    public BMAP_NAVIGATION_CONTROL_ZOOM: number = 3;

    constructor(private mapBaiduService: MapBaiduService) {
        super();
    }

    public ngAfterViewInit(): void {
        this.mapBaiduService.createMap(this.mapInstance.nativeElement);
        this.mapBaiduService.addMapOptions();

        if (!this.isInMobile) {
            this.mapBaiduService.addMapControls({
                anchor: this.BMAP_ANCHOR_BOTTOM_RIGHT,
                type: this.BMAP_NAVIGATION_CONTROL_ZOOM,
            });
        }
    }

    public ngOnInit(): void {
        this.subscription.add(
            this.mapBaiduService.mapInitialized$.subscribe(
                (isMapInitialized: boolean) => {
                    this.mapInitialized.next(isMapInitialized);
                },
            ),
        );

        this.subscription.add(
            this.mapBaiduService.mapInitialized$
                .pipe(
                    filter(Boolean),
                    switchMap(() => this.mapEvent$),
                )
                .subscribe((mapEvent) => {
                    switch (mapEvent.mode) {
                        case "default": {
                            this.defaultMode(mapEvent.center);
                            break;
                        }
                        case "at-least-one-marker": {
                            this.atLeastOneMarkerMode(
                                mapEvent.center,
                                mapEvent.bounds,
                            );
                            break;
                        }
                        case "specific-points": {
                            if (mapEvent.points.length === 1) {
                                this.defaultMode(mapEvent.points[0]);
                                break;
                            }
                            this.specificMarkersMode(mapEvent.points);
                            break;
                        }
                        case "all-markers": {
                            this.allMarkersMode();
                            break;
                        }
                    }
                }),
        );

        this.subscription.add(
            this.mapBaiduService
                .getMovingEventListener()
                .pipe(
                    debounceTime(100),
                    switchMap(() => this.mapBaiduService.getBounds()),
                    map((bounds) => this.getMarkersCoordinatesInBounds(bounds)),
                    withLatestFrom(this.markersInBounds$),
                    filter(
                        ([currentMarkerData, previousMarkerData]) =>
                            !this.compareMarkersList(
                                currentMarkerData,
                                previousMarkerData,
                            ),
                    ),
                )
                .subscribe(([currentMarkerData]) => {
                    this.markersInBounds$.next(currentMarkerData);
                    this.markersCoordinatesInBounds.emit(currentMarkerData);
                }),
        );
    }

    public compareMarkersList(
        markersA: MarkerData[],
        markersB: MarkerData[],
    ): boolean {
        return (
            markersA.length === markersB.length &&
            markersA.every((marker, index) => marker === markersB[index])
        );
    }

    public defaultMode(coordinates: Coordinates): void {
        const point = getBMapPoint(coordinates);
        this.mapBaiduService.setCenterAndZoom(point, DEFAULT_ZOOM_LEVEL);
    }

    public atLeastOneMarkerMode(center: Coordinates, bounds?: MapBounds): void {
        const baiduBounds = new BMap.Bounds(0, 0, 0, 0);

        if (bounds) {
            baiduBounds.minX = bounds.west;
            baiduBounds.minY = bounds.south;
            baiduBounds.maxX = bounds.east;
            baiduBounds.maxY = bounds.north;
        } else {
            extendBounds(center, baiduBounds);
        }

        // If there are no visible markers in the bounds, then add the one closest to the center
        if (this.getMarkersCoordinatesInBounds(baiduBounds).length === 0) {
            const closestMarker = getClosestMarker(this.markers, center);
            extendBounds(closestMarker.coordinates, baiduBounds);
        }
        this.mapBaiduService.setViewport(baiduBounds);
    }

    public getMarkersCoordinatesInBounds(bounds: BMap.Bounds): MarkerData[] {
        return this.markers.filter((marker) =>
            bounds.containsPoint(getBMapPoint(marker.coordinates)),
        );
    }

    public specificMarkersMode(coordinates: Coordinates[]): void {
        const points: BMap.Point[] = [];
        coordinates.forEach((coordinate) => {
            const point = getBMapPoint(coordinate);
            points.push(point);
        });
        this.mapBaiduService.setViewportPoint(points);
    }

    public allMarkersMode(): void {
        const points: BMap.Point[] = [];
        this.markers.forEach((marker) => {
            const point = getBMapPoint(marker.coordinates);
            points.push(point);
        });

        this.mapBaiduService.setViewportPoint(points);
    }

    public ngOnDestroy(): void {
        this.mapBaiduService.resetService();
        this.subscription.unsubscribe();
    }
}
